import { useEffect, useState } from 'react'
import App, { AppContext, AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import initAuth from '@frontend/lib/cognitoUserPool'
import { dayjs } from '@frontend/lib/dayjs-plugins'
import { setStoreLocale } from '@frontend/stores/CalendarBookingStore'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConfigProvider } from 'antd'
import { App as AntdApp } from 'antd'
import enGB from 'antd/locale/en_GB'
import itIT from 'antd/locale/it_IT'

import 'antd/dist/reset.css'
import 'dayjs/locale/en'
import 'dayjs/locale/it'
import '@frontend/lib/dayjs-plugins'
import '@frontend/styles/ShipmentDetails-global.scss'
import '@frontend/styles/calendar-global.scss'
import '@frontend/styles/globals.scss'
import '@frontend/styles/shipment-create-global.css'
import '@frontend/styles/table-global.scss'

initAuth()

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 1000 * 30 } } })

const setDayjsLocale = (nextLocale?: string) => {
  const locale = nextLocale === 'it' ? 'it' : 'en'
  dayjs.locale(locale)
  // Dayjs instances already created (e.g. in stores) keep their original locale, so re-apply it.
  setStoreLocale(locale)
}

const getLocale = (nextLocale?: string) => {
  return nextLocale === 'it' ? itIT : enGB
}

function CustomApp({ Component, pageProps }: AppProps) {
  const router = useRouter()

  useEffect(() => {
    setDayjsLocale(router.locale)
  }, [router.locale])

  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  // Let React own the holder style's lifecycle; removing it via the DOM breaks reconciliation (removeChild errors).
  const [showHolderStyle, setShowHolderStyle] = useState(true)
  useEffect(() => {
    const removeHolderStyle = () => {
      setShowHolderStyle(false)
    }
    // Wait for the full 'load' event (not just mount) so transitions stay disabled until assets have loaded.
    if (document.readyState === 'complete') {
      removeHolderStyle()
      return
    }
    window.addEventListener('load', removeHolderStyle)
    return () => {
      window.removeEventListener('load', removeHolderStyle)
    }
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <Head>
        <link rel="shortcut icon" href="/favicon.svg" />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <ConfigProvider
        locale={getLocale(router.locale)}
        theme={{
          hashed: false, // Workaround for the hash in the class names issue #https://github.com/ant-design/ant-design/discussions/38753
          components: {
            Menu: {
              colorText: 'unset',
            },
            Table: {
              headerColor: '#8c8c8c',
            },
          },
        }}
      >
        {showHolderStyle && (
          <style
            id="holderStyle"
            dangerouslySetInnerHTML={{
              __html: `
                    *, *::before, *::after {
                        transition: none!important;
                    }
                    `,
            }}
          />
        )}
        <div style={{ visibility: !mounted ? 'hidden' : 'visible' }}>
          <AntdApp>
            <Component {...pageProps} />
          </AntdApp>
        </div>
      </ConfigProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}

CustomApp.getInitialProps = async (appContext: AppContext) => {
  // Force SSR for all pages so that runtime environment variables work
  const appProps = await App.getInitialProps(appContext)
  return { ...appProps }
}

export default appWithTranslation(CustomApp)
